import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Popover,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import PrintIcon from "@mui/icons-material/Print";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { tokens } from "../../theme";
import { API_BASE_URL } from "../../config/api";
import BookingDetailDialog from "./BookingDetailDialog";
import DocketsPreviewDialog from "./DocketsPreviewDialog";
import { printDepartureDockets } from "./printDockets";
import * as docketHelpers from "./tapUtils";
import {
  GOLD,
  MONTHS_FULL,
  REF_BG,
  WEEKDAYS,
  addDays,
  formatDateLabel,
  formatTime,
  getCalendarCells,
  groupByProduct,
  isSameDay,
  productTypeCode,
  productTypeColor,
  startOfDay,
  terminalNumber,
  toDateKey,
} from "./tapUtils";

const DailyTap = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const today = startOfDay(new Date());
  const [selectedDate, setSelectedDate] = useState(today);
  const [viewMonth, setViewMonth] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const [tab, setTab] = useState("departures");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [docketsOpen, setDocketsOpen] = useState(false);

  const fetchTapBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/signup/getTapBookings`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) throw new Error("Failed to fetch TAP bookings");
      const data = await response.json();
      if (data.subCode === 200 && Array.isArray(data.items)) {
        setBookings(data.items);
      } else {
        setError("Failed to load TAP bookings: " + (data.message || "Unknown error"));
      }
    } catch (err) {
      setError(err.message);
      console.error("Daily TAP fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTapBookings();
  }, []);

  const filteredByType = useMemo(() => {
    const active = bookings.filter(
      (booking) => String(booking.bookingStatus || "").toUpperCase() !== "CANCELLED"
    );
    if (typeFilter === "ALL") return active;
    return active.filter((booking) => productTypeCode(booking.productType) === typeFilter);
  }, [bookings, typeFilter]);

  const departures = useMemo(() => {
    return filteredByType
      .filter((booking) => {
        const date = docketHelpers.parseTapDateTime(booking.arrivalDateTime);
        return date && isSameDay(date, selectedDate);
      })
      .sort((a, b) => {
        const left = docketHelpers.parseTapDateTime(a.arrivalDateTime)?.getTime() || 0;
        const right = docketHelpers.parseTapDateTime(b.arrivalDateTime)?.getTime() || 0;
        return left - right;
      });
  }, [filteredByType, selectedDate]);

  const returns = useMemo(() => {
    return filteredByType
      .filter((booking) => {
        const date = docketHelpers.parseTapDateTime(booking.returnDateTime);
        return date && isSameDay(date, selectedDate);
      })
      .sort((a, b) => {
        const left = docketHelpers.parseTapDateTime(a.returnDateTime)?.getTime() || 0;
        const right = docketHelpers.parseTapDateTime(b.returnDateTime)?.getTime() || 0;
        return left - right;
      });
  }, [filteredByType, selectedDate]);

  const activeList = tab === "departures" ? departures : returns;
  const groupedList = groupByProduct(activeList);
  const dateKey = toDateKey(selectedDate);

  const openCalendar = (event) => {
    setViewMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
    setCalendarAnchor(event.currentTarget);
  };

  const selectCalendarDate = (date) => {
    setSelectedDate(startOfDay(date));
    setCalendarAnchor(null);
  };

  const openDockets = () => {
    if (!departures.length) {
      alert("No departures to print for this date.");
      return;
    }
    setDocketsOpen(true);
  };

  const downloadDocketsPdf = async () => {
    if (!departures.length) {
      alert("No departures to print for this date.");
      return;
    }
    try {
      await printDepartureDockets(departures, dateKey, docketHelpers);
    } catch (err) {
      alert(err.message);
    }
  };

  const renderBookingRow = (booking) => {
    const isDeparture = tab === "departures";
    const timeValue = isDeparture ? booking.arrivalDateTime : booking.returnDateTime;
    const terminal = isDeparture ? booking.arrivalTerminal : booking.returnTerminal;
    const code = productTypeCode(booking.productType);
    return (
      <Box
        key={`${booking._id}-${tab}`}
        display="flex"
        alignItems="center"
        gap={1.5}
        px={2}
        py={1.25}
        onClick={() => setSelectedBooking(booking)}
        sx={{
          borderBottom: `1px solid ${colors.primary[500]}`,
          cursor: "pointer",
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "dark" ? colors.primary[500] : "rgba(0,0,0,0.04)",
          },
          "&:last-of-type": { borderBottom: "none" },
        }}
      >
        <Typography
          minWidth={52}
          fontWeight={700}
          color={colors.grey[100]}
          sx={{ fontSize: "1.05rem" }}
        >
          {formatTime(timeValue)}
        </Typography>
        <Box
          px={1.2}
          py={0.4}
          borderRadius="12px"
          bgcolor={REF_BG}
          color="#2d1b4e"
          fontWeight={700}
          fontSize="0.85rem"
          sx={{ whiteSpace: "nowrap" }}
        >
          {booking.referenceNumber || "—"}
        </Box>
        <Box
          px={0.8}
          py={0.35}
          borderRadius="4px"
          bgcolor={productTypeColor(code)}
          color="#fff"
          fontWeight={700}
          fontSize="0.75rem"
          minWidth={34}
          textAlign="center"
        >
          {code === "SELF" ? "SP" : code}
        </Box>
        <Typography
          flex={1}
          color={colors.grey[100]}
          sx={{
            fontSize: "0.95rem",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {booking.name || "—"}
        </Typography>
        <Typography minWidth={18} textAlign="center" color={colors.grey[200]} fontWeight={600}>
          {terminalNumber(terminal)}
        </Typography>
        <Box
          px={1.2}
          py={0.45}
          borderRadius="12px"
          bgcolor={GOLD}
          color="#1a1a1a"
          fontWeight={800}
          fontSize="0.9rem"
          letterSpacing="0.4px"
          sx={{ whiteSpace: "nowrap", minWidth: 88, textAlign: "center" }}
        >
          {booking.reg || "TBC"}
        </Box>
      </Box>
    );
  };

  return (
    <Box m="20px">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        px={2.5}
        py={1.5}
        mb={2}
        borderRadius="6px"
        sx={{ backgroundColor: GOLD }}
      >
        <Typography variant="h3" fontWeight={800} color="#1a1a1a">
          Daily {dateKey}
        </Typography>
        <Box display="flex" alignItems="center" gap={1.5}>
          <ButtonGroup variant="contained" size="small">
            {[
              { id: "ALL", label: "All" },
              { id: "MG", label: "MG" },
              { id: "PR", label: "PR" },
              { id: "SELF", label: "Self Park" },
            ].map((option) => (
              <Button
                key={option.id}
                onClick={() => setTypeFilter(option.id)}
                sx={{
                  backgroundColor: typeFilter === option.id ? "#1a1a1a" : "#fff",
                  color: typeFilter === option.id ? GOLD : "#1a1a1a",
                  fontWeight: 700,
                  "&:hover": {
                    backgroundColor: typeFilter === option.id ? "#333" : "#f3f3f3",
                  },
                }}
              >
                {option.label}
              </Button>
            ))}
          </ButtonGroup>
          <IconButton
            onClick={fetchTapBookings}
            sx={{ backgroundColor: "#fff", "&:hover": { backgroundColor: "#f3f3f3" } }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
        <Button
          variant="contained"
          onClick={openCalendar}
          sx={{
            backgroundColor: colors.primary[400],
            color: colors.grey[100],
            fontWeight: 700,
            fontSize: "1.05rem",
            px: 3,
            py: 1.2,
            textTransform: "none",
            "&:hover": { backgroundColor: colors.primary[400], opacity: 0.92 },
          }}
        >
          {formatDateLabel(selectedDate, today)}
        </Button>
        <Box display="flex" alignItems="center" gap={2} mt={1}>
          <IconButton onClick={() => setSelectedDate(addDays(selectedDate, -1))} color="inherit">
            <ChevronLeftIcon />
          </IconButton>
          <Button
            onClick={() => setSelectedDate(today)}
            sx={{ color: colors.grey[100], fontWeight: 700, textTransform: "none" }}
          >
            Today
          </Button>
          <IconButton onClick={() => setSelectedDate(addDays(selectedDate, 1))} color="inherit">
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      <Popover
        open={Boolean(calendarAnchor)}
        anchorEl={calendarAnchor}
        onClose={() => setCalendarAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box p={2} width={300} bgcolor={colors.primary[400]}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Typography fontWeight={800} color={colors.grey[100]}>
              {MONTHS_FULL[viewMonth.getMonth()]} {viewMonth.getFullYear()}
            </Typography>
            <Box>
              <IconButton
                size="small"
                onClick={() =>
                  setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))
                }
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() =>
                  setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))
                }
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" mb={0.5}>
            {WEEKDAYS.map((day, index) => (
              <Typography
                key={`${day}-${index}`}
                textAlign="center"
                fontSize="0.75rem"
                color={colors.grey[300]}
                fontWeight={700}
              >
                {day}
              </Typography>
            ))}
          </Box>
          <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={0.5}>
            {getCalendarCells(viewMonth).map((cell, index) => {
              if (!cell) return <Box key={`empty-${index}`} height={36} />;
              const selected = isSameDay(cell, selectedDate);
              const isTodayCell = isSameDay(cell, today);
              return (
                <Button
                  key={toDateKey(cell)}
                  onClick={() => selectCalendarDate(cell)}
                  aria-label={`Select ${toDateKey(cell)}`}
                  sx={{
                    minWidth: 36,
                    height: 36,
                    p: 0,
                    borderRadius: "50%",
                    backgroundColor: selected ? GOLD : "transparent",
                    color: selected ? "#1a1a1a" : colors.grey[100],
                    fontWeight: selected || isTodayCell ? 800 : 500,
                    border: isTodayCell && !selected ? `1px solid ${GOLD}` : "none",
                    "&:hover": {
                      backgroundColor: selected ? GOLD : colors.primary[500],
                    },
                  }}
                >
                  {cell.getDate()}
                </Button>
              );
            })}
          </Box>
        </Box>
      </Popover>

      <Box bgcolor={colors.primary[400]} borderRadius="8px" overflow="hidden">
        <Tabs
          value={tab}
          onChange={(_event, value) => setTab(value)}
          TabIndicatorProps={{ style: { backgroundColor: GOLD, height: 3 } }}
          sx={{
            px: 2,
            borderBottom: `1px solid ${colors.primary[500]}`,
            "& .MuiTab-root": {
              color: colors.grey[300],
              fontWeight: 700,
              fontSize: "1rem",
              textTransform: "none",
            },
            "& .Mui-selected": { color: `${GOLD} !important` },
          }}
        >
          <Tab value="departures" label={`${departures.length} Departures`} />
          <Tab value="returns" label={`${returns.length} Returns`} />
        </Tabs>

        <Box px={2.5} py={1.5} display="flex" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography variant="h4" fontWeight={800} color={colors.grey[100]}>
            {tab === "departures"
              ? `${departures.length} Departures`
              : `${returns.length} Returns`}
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={openDockets}
              disabled={!departures.length}
              sx={{ backgroundColor: GOLD, color: "#1a1a1a", fontWeight: 800, "&:hover": { backgroundColor: "#e0b000" } }}
            >
              Dockets
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadOutlinedIcon />}
              onClick={downloadDocketsPdf}
              disabled={!departures.length}
              sx={{ borderColor: GOLD, color: GOLD, fontWeight: 700 }}
            >
              Download PDF
            </Button>
          </Box>
        </Box>

        {loading && (
          <Box px={2.5} py={4}>
            Loading daily TAP bookings...
          </Box>
        )}
        {error && (
          <Box px={2.5} py={4} color={colors.redAccent[400]}>
            Error: {error}
          </Box>
        )}
        {!loading && !error && activeList.length === 0 && (
          <Box px={2.5} py={4} color={colors.grey[300]}>
            No {tab} for {formatDateLabel(selectedDate, today)}.
          </Box>
        )}
        {!loading &&
          !error &&
          groupedList.map((group) => (
            <Box key={group.productName}>
              <Box
                px={2.5}
                py={0.8}
                sx={{
                  backgroundColor: "rgba(245, 197, 24, 0.22)",
                  color: GOLD,
                  fontWeight: 700,
                  fontSize: "0.9rem",
                }}
              >
                {group.productName}
              </Box>
              {group.bookings.map(renderBookingRow)}
            </Box>
          ))}
      </Box>

      <BookingDetailDialog
        open={Boolean(selectedBooking)}
        booking={selectedBooking}
        onClose={() => setSelectedBooking(null)}
      />
      <DocketsPreviewDialog
        open={docketsOpen}
        bookings={departures}
        dateKey={dateKey}
        onClose={() => setDocketsOpen(false)}
      />
    </Box>
  );
};

export default DailyTap;
